import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Edit, 
  Trash2, 
  Play, 
  MoreVertical,
  Calendar,
  Smartphone,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react'
import { AppConfig, AppStatus, Platform } from '../types'
import { mockApiCalls } from '../utils/mockData'

type SortOption = 'name' | 'created' | 'updated' | 'status'
type FilterOption = 'all' | 'draft' | 'configured' | 'building' | 'completed' | 'failed'

export function AppHistory() {
  const [apps, setApps] = useState<AppConfig[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<SortOption>('updated')
  const [filterBy, setFilterBy] = useState<FilterOption>('all')
  const [selectedApps, setSelectedApps] = useState<string[]>([])

  useEffect(() => {
    const loadApps = async () => {
      try {
        const appsData = await mockApiCalls.getApps()
        setApps(appsData)
      } catch (error) {
        console.error('Failed to load apps:', error)
      } finally {
        setLoading(false)
      }
    }

    loadApps()
  }, [])

  const getStatusIcon = (status: AppStatus) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-success-500" />
      case 'failed':
        return <XCircle className="w-4 h-4 text-error-500" />
      case 'building':
        return <Clock className="w-4 h-4 text-brand-500 animate-pulse" />
      case 'configured':
        return <AlertCircle className="w-4 h-4 text-warning-500" />
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />
    }
  }

  const getStatusColor = (status: AppStatus) => {
    switch (status) {
      case 'completed':
        return 'text-success-600 bg-success-50 border-success-200'
      case 'failed':
        return 'text-error-600 bg-error-50 border-error-200'
      case 'building':
        return 'text-brand-600 bg-brand-50 border-brand-200'
      case 'configured':
        return 'text-warning-600 bg-warning-50 border-warning-200'
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200'
    }
  }

  const getPlatformIcon = (platform: Platform) => {
    return <Smartphone className={`w-4 h-4 ${platform === 'ios' ? 'text-gray-700' : 'text-green-600'}`} />
  }

  const filteredAndSortedApps = apps
    .filter(app => {
      if (searchTerm && !app.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !app.description.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false
      }
      
      if (filterBy !== 'all' && app.status !== filterBy) {
        return false
      }
      
      return true
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name)
        case 'created':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        case 'updated':
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        case 'status':
          return a.status.localeCompare(b.status)
        default:
          return 0
      }
    })

  const handleSelectApp = (appId: string) => {
    setSelectedApps(prev => 
      prev.includes(appId) 
        ? prev.filter(id => id !== appId)
        : [...prev, appId]
    )
  }

  const handleSelectAll = () => {
    if (selectedApps.length === filteredAndSortedApps.length) {
      setSelectedApps([])
    } else {
      setSelectedApps(filteredAndSortedApps.map(app => app.id))
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-gray-200 rounded w-1/3 animate-pulse"></div>
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 bg-gray-200 rounded-lg animate-pulse"></div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Apps</h1>
          <p className="text-gray-600 mt-1">Manage and monitor your mobile app projects</p>
        </div>
        <Link 
          to="/configure" 
          className="btn-primary flex items-center space-x-2"
        >
          <Smartphone className="w-4 h-4" />
          <span>Create New App</span>
        </Link>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search apps..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <select
                value={filterBy}
                onChange={(e) => setFilterBy(e.target.value as FilterOption)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="configured">Configured</option>
                <option value="building">Building</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="updated">Sort by Updated</option>
              <option value="created">Sort by Created</option>
              <option value="name">Sort by Name</option>
              <option value="status">Sort by Status</option>
            </select>
          </div>
        </div>

        {selectedApps.length > 0 && (
          <div className="mt-4 p-3 bg-brand-50 border border-brand-200 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-brand-900">
                {selectedApps.length} app{selectedApps.length !== 1 ? 's' : ''} selected
              </span>
              <div className="flex items-center space-x-2">
                <button className="text-sm text-gray-600 hover:text-gray-800 px-3 py-1 rounded border border-gray-300 hover:bg-gray-50">
                  Archive
                </button>
                <button className="text-sm text-error-600 hover:text-error-800 px-3 py-1 rounded border border-error-300 hover:bg-error-50">
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {filteredAndSortedApps.length === 0 ? (
          <div className="text-center py-12">
            <Smartphone className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No apps found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || filterBy !== 'all' 
                ? 'Try adjusting your search or filters.'
                : 'Get started by creating your first app.'
              }
            </p>
            {!searchTerm && filterBy === 'all' && (
              <div className="mt-4">
                <Link to="/configure" className="btn-primary">
                  Create New App
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredAndSortedApps.map((app) => (
              <div key={app.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center">
                  <div className="flex items-center flex-1 min-w-0">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedApps.includes(app.id)}
                        onChange={() => handleSelectApp(app.id)}
                        className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                      />
                    </label>
                    
                    <div className="ml-3 flex items-center space-x-3 flex-1 min-w-0">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-sm font-medium flex-shrink-0"
                        style={{ backgroundColor: app.branding.primaryColor }}
                      >
                        {app.name.charAt(0).toUpperCase()}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-gray-900 truncate">
                          {app.name}
                        </h3>
                        <p className="text-sm text-gray-500 truncate">
                          {app.description}
                        </p>
                        <div className="flex items-center space-x-4 mt-1">
                          <span className="text-xs text-gray-400">
                            v{app.version}
                          </span>
                          <span className="text-xs text-gray-400">
                            {app.packageName}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(app.status)}`}>
                      {getStatusIcon(app.status)}
                      <span className="capitalize">{app.status}</span>
                    </div>

                    <div className="flex items-center space-x-2 w-32 flex-shrink-0 justify-end">
                      <Link
                        to={`/apps/${app.id}`}
                        className="p-1 text-gray-400 hover:text-gray-600 rounded"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </Link>
                      
                      <Link
                        to={`/apps/${app.id}/edit`}
                        className="p-1 text-gray-400 hover:text-gray-600 rounded"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </Link>

                      {app.status === 'configured' && (
                        <button
                          onClick={() => alert('Start build functionality would be implemented here')}
                          className="p-1 text-brand-500 hover:text-brand-600 rounded"
                          title="Start Build"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
} 